//codex-- Give the WIP calendar the shared toolbar design while retaining its own calendar controls.
import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon, ClipboardDocumentListIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

//codex-- Keep the original scheduler/new/dashboard colors in lighter shades with readable labels.
const BUTTON_CLASS = 'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';
const COLORS = {
  yellow: 'border-yellow-200 bg-yellow-100 text-yellow-900 hover:bg-yellow-200 focus-visible:outline-yellow-600',
  orange: 'border-orange-200 bg-orange-100 text-orange-900 hover:bg-orange-200 focus-visible:outline-orange-600',
  purple: 'border-purple-200 bg-purple-100 text-purple-900 hover:bg-purple-200 focus-visible:outline-purple-600',
  blue: 'border-blue-200 bg-blue-100 text-blue-900 hover:bg-blue-200 focus-visible:outline-blue-600',
};
const FIELD_CLASS = 'block h-11 w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20';

//codex-- Date inputs represent local calendar dates rather than a UTC-shifted day.
const localDateValue = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

//codex-- Department changes wait for Apply; calendar view/date navigation remains immediate.
export default function WipSchedulerActionBar({
  viewMode, onViewChange, selectedDept, departments, onDepartmentChange,
  selectedDay, onDayChange, selectedMonth, selectedYear, onMonthChange,
  selectedDates, onPreviousWeek, onNextWeek, onNewSchedule, loading,
}) {
  const appliedDepartment = selectedDept || 'All';
  const [draftDepartment, setDraftDepartment] = useState(appliedDepartment);
  const previousDepartment = useRef(appliedDepartment);
  useEffect(() => {
    //codex-- Capture the prior applied value before React schedules the draft update.
    const previous = previousDepartment.current;
    if (previous !== appliedDepartment) {
      setDraftDepartment((draft) => draft === previous ? appliedDepartment : draft);
      previousDepartment.current = appliedDepartment;
    }
  }, [appliedDepartment]);
  const options = [...new Set(['All', ...departments.filter(Boolean), appliedDepartment])];
  const weekReady = selectedDates.length === 7;
  const weekLabel = weekReady ? `${selectedDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${selectedDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Loading week…';

  //codex-- Applying or clearing the department preserves the calendar's selected view and date range.
  const applyDepartment = (event) => {
    event.preventDefault();
    if (!loading) onDepartmentChange(draftDepartment);
  };
  const clearDepartment = () => {
    setDraftDepartment('All');
    onDepartmentChange('All');
  };

  return (
    //codex-- Keep every scheduler action visible and wrap the navigation on small screens.
    <section aria-label="WIP tools and calendar controls" className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <p className="text-sm font-semibold text-slate-800">WIP tools</p>
        <nav aria-label="WIP tools" className="flex flex-wrap gap-2">
          <NavLink to="/full_scheduler_viewer" className={`${BUTTON_CLASS} ${COLORS.yellow}`}>
            <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0" />Full scheduler
          </NavLink>
          <button type="button" onClick={onNewSchedule} className={`${BUTTON_CLASS} ${COLORS.orange}`}>
            <PlusIcon aria-hidden="true" className="h-4 w-4 shrink-0" />New schedule
          </button>
          <NavLink to="/wip_scheduler_list" className={`${BUTTON_CLASS} ${COLORS.purple}`}>
            <ClipboardDocumentListIcon aria-hidden="true" className="h-4 w-4 shrink-0" />WIP dashboard
          </NavLink>
        </nav>
      </div>

      {/*codex-- Label view/department controls and show the date control appropriate to the active view. */}
      <form onSubmit={applyDepartment} className="p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-2">
            <label htmlFor="wip-calendar-view" className="mb-1.5 block text-xs font-semibold text-slate-600">Calendar view</label>
            <select id="wip-calendar-view" className={FIELD_CLASS} value={viewMode} onChange={onViewChange}>
              <option value="day">Day view</option>
              <option value="week">Week view</option>
              <option value="month">Month view</option>
            </select>
          </div>
          <div className="lg:col-span-3">
            <label htmlFor="wip-department" className="mb-1.5 block text-xs font-semibold text-slate-600">Department</label>
            <select id="wip-department" className={FIELD_CLASS} value={draftDepartment} onChange={(event) => setDraftDepartment(event.target.value)}>
              {options.map((department) => <option key={department} value={department}>{department === 'All' ? 'All departments' : department}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2 lg:col-span-7">
            {viewMode === 'week' && (
              <>
                <p id="wip-week-label" className="mb-1.5 text-xs font-semibold text-slate-600">Calendar week</p>
                <div className="flex min-h-[44px] items-center gap-2" aria-labelledby="wip-week-label">
                  <button type="button" aria-label="Previous week" disabled={!weekReady}
                    onClick={onPreviousWeek} className={`${BUTTON_CLASS} ${COLORS.blue} shrink-0 disabled:opacity-50`}>
                    <ChevronLeftIcon aria-hidden="true" className="h-4 w-4" />
                  </button>
                  <span className="min-w-0 flex-1 text-center text-sm font-medium text-slate-700" aria-live="polite">{weekLabel}</span>
                  <button type="button" aria-label="Next week" disabled={!weekReady}
                    onClick={onNextWeek} className={`${BUTTON_CLASS} ${COLORS.blue} shrink-0 disabled:opacity-50`}>
                    <ChevronRightIcon aria-hidden="true" className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
            {viewMode === 'day' && (
              <>
                <label htmlFor="wip-calendar-day" className="mb-1.5 block text-xs font-semibold text-slate-600">Calendar day</label>
                <input id="wip-calendar-day" type="date" className={FIELD_CLASS} value={localDateValue(selectedDay)}
                  onChange={(event) => { if (event.target.value) onDayChange(event); }} />
              </>
            )}
            {viewMode === 'month' && (
              <>
                <label htmlFor="wip-calendar-month" className="mb-1.5 block text-xs font-semibold text-slate-600">Calendar month</label>
                <input id="wip-calendar-month" type="month" className={FIELD_CLASS}
                  value={`${selectedYear}-${String(selectedMonth).padStart(2, '0')}`}
                  onChange={(event) => { if (event.target.value) onMonthChange(event); }} />
              </>
            )}
          </div>
        </div>

        {/*codex-- Use the same Apply/Clear controls while explaining that they affect the department filter. */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span role="status" className="text-xs text-slate-500">
            {draftDepartment !== appliedDepartment ? 'Unapplied department change' : 'View and dates update immediately. Apply changes the department.'}
          </span>
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <button type="button" onClick={clearDepartment} className={`${BUTTON_CLASS} ${COLORS.orange}`}>
              <XMarkIcon aria-hidden="true" className="h-4 w-4" />Clear filters
            </button>
            <button type="submit" disabled={loading} className={`${BUTTON_CLASS} ${COLORS.blue} disabled:cursor-not-allowed disabled:opacity-50`}>
              Apply filters
            </button>
          </div>
        </div>
      </form>

      {/*codex-- Show the applied department with a removable label rather than silently hiding other teams. */}
      {appliedDepartment !== 'All' && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-3 sm:px-5">
          <span className="text-xs font-semibold text-slate-500">Applied</span>
          <button type="button" aria-label="Remove department filter" onClick={clearDepartment}
            className="inline-flex items-center gap-1.5 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            Department: {appliedDepartment}<XMarkIcon aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </section>
  );
}
